import { useNavigate } from 'react-router-dom';
import { Building2, Star, Rocket, ShieldCheck, ArrowRight } from 'lucide-react';
import { useAdminCompanies, useAdminPlanRequests } from '@/api/queries/superAdmin.queries';
import { PageHeader } from '@/components/shared/PageHeader';

function StatCard({ icon: Icon, label, value, color, bg, onClick }) {
  return (
    <button onClick={onClick} className="card flex items-center gap-4 text-left p-5 hover:border-[var(--accent)] transition-colors border border-transparent">
      <div className="w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0" style={{ backgroundColor: bg }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div className="flex-1">
        <p className="text-[24px] font-black text-[var(--text-primary)]">{value}</p>
        <p className="text-[12px] text-[var(--text-secondary)]">{label}</p>
      </div>
      <ArrowRight size={15} className="text-[var(--text-tertiary)] shrink-0" />
    </button>
  );
}

export default function SuperAdminOverviewPage() {
  const navigate = useNavigate();
  const { data: companiesResp } = useAdminCompanies();
  const { data: proResp } = useAdminPlanRequests({ type: 'PRO_UPGRADE', status: 'PENDING' });
  const { data: customResp } = useAdminPlanRequests({ type: 'CUSTOM_REGISTERED,CUSTOM_LEAD', status: 'PENDING' });

  const companies = companiesResp?.data ?? [];
  const basicCount = companies.filter(c => c.plan === 'BASIC').length;
  const proCount = companies.filter(c => c.plan === 'PRO').length;
  const customCount = companies.filter(c => c.plan === 'CUSTOM').length;
  const pendingPro = proResp?.data?.length ?? 0;
  const pendingCustom = customResp?.data?.length ?? 0;

  return (
    <div className="space-y-5 fade-in max-w-5xl">
      <PageHeader
        title="Super Admin"
        subtitle="Platform-wide view across every AttendX company"
        breadcrumb={[{ label: 'Super Admin' }]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Building2} label="Total companies" value={companies.length} color="#6C6C70" bg="rgba(108,108,112,0.1)" onClick={() => navigate('/super-admin/basic-users')} />
        <StatCard icon={Star} label="Pro requests pending" value={pendingPro} color="#007AFF" bg="rgba(0,122,255,0.1)" onClick={() => navigate('/super-admin/pro-requests')} />
        <StatCard icon={Rocket} label="Custom requests pending" value={pendingCustom} color="#AF52DE" bg="rgba(175,82,222,0.1)" onClick={() => navigate('/super-admin/custom-requests')} />
        <StatCard icon={ShieldCheck} label="Plan management" value="→" color="#34C759" bg="rgba(52,199,89,0.1)" onClick={() => navigate('/super-admin/plan-management')} />
      </div>

      <div className="card p-5 sm:p-6">
        <p className="text-[13px] font-bold text-[var(--text-primary)] mb-4">Companies by plan</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            ['Basic', basicCount, '#6C6C70'],
            ['Pro', proCount, '#007AFF'],
            ['Custom', customCount, '#AF52DE'],
          ].map(([label, count, color]) => (
            <div key={label} className="rounded-[12px] bg-[var(--bg-muted)] p-4 text-center">
              <p className="text-[20px] font-black" style={{ color }}>{count}</p>
              <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { to: '/super-admin/basic-users', title: 'Basic Registered Users', desc: 'Every company currently on the free Basic plan.' },
          { to: '/super-admin/pro-requests', title: 'Pro Plan Requests', desc: 'Approve or reject self-serve Pro upgrades.' },
          { to: '/super-admin/custom-requests', title: 'Custom Plan Requests', desc: 'Registered customers + unregistered leads needing a callback.' },
          { to: '/super-admin/custom-companies', title: 'Custom Companies', desc: 'Edit per-company pricing, limits and features for every Custom customer.' },
        ].map(s => (
          <button key={s.to} onClick={() => navigate(s.to)} className="card text-left p-5 hover:border-[var(--accent)] transition-colors border border-transparent">
            <p className="text-[14px] font-bold text-[var(--text-primary)] mb-1">{s.title}</p>
            <p className="text-[12px] text-[var(--text-secondary)]">{s.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
